function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inline(value) {
  return escapeHtml(value)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, function (match, text, href) {
      if (href.charAt(0) === "#") {
        return '<a href="#" data-manual-id="' + escapeHtml(href.slice(1)) + '">' + text + "</a>";
      }
      const safe = href.indexOf("http://") === 0 || href.indexOf("https://") === 0 ? href : "#";
      return '<a href="' + escapeHtml(safe) + '" target="_blank" rel="noopener">' + text + "</a>";
    })
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
}

const imageContext = require.context("./img", false, /\.png$/);
const imageMap = {};
imageContext.keys().forEach(function (key) {
  imageMap[key.replace("./", "")] = imageContext(key);
});

function imageSrc(src) {
  const file = String(src || "").split("/").pop();
  return imageMap[file] || src;
}

export function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function slug(value) {
  const text = normalizeText(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return text || "cast";
}

function parseTable(rows) {
  const parsed = rows.map((row) =>
    row
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((cell) => cell.trim())
  );
  const body = parsed.filter(
    (cells) => !cells.every((cell) => /^:?-+:?$/.test(cell))
  );
  if (!body.length) {
    return { type: "html", html: "", plain: "" };
  }
  const head = body[0];
  const rest = body.slice(1);
  let html = '<table class="table table-sm table-bordered manual-table"><thead><tr>';
  head.forEach((cell) => {
    html += "<th>" + inline(cell) + "</th>";
  });
  html += "</tr></thead><tbody>";
  rest.forEach((row) => {
    html += "<tr>";
    row.forEach((cell) => {
      html += "<td>" + inline(cell) + "</td>";
    });
    html += "</tr>";
  });
  html += "</tbody></table>";
  html = '<div class="table-responsive">' + html + "</div>";
  return {
    type: "html",
    html: html,
    plain: body.map((row) => row.join(" ")).join("\n")
  };
}

function renderBulletList(items) {
  function render(level, index) {
    let html = "<ul>";
    const plain = [];
    while (index < items.length && items[index].level === level) {
      const text = items[index].text;
      plain.push(text);
      index += 1;
      let child = "";
      if (index < items.length && items[index].level > level) {
        const nested = render(items[index].level, index);
        child = nested.html;
        plain.push.apply(plain, nested.plain);
        index = nested.index;
      }
      html += "<li>" + inline(text) + child + "</li>";
    }
    html += "</ul>";
    return { html: html, plain: plain, index: index };
  }

  const firstLevel = items.length ? items[0].level : 0;
  const rendered = render(firstLevel, 0);
  return {
    type: "html",
    html: rendered.html,
    plain: rendered.plain.join("\n")
  };
}

function parseBlocks(markdown) {
  const lines = String(markdown || "")
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .split("\n");
  const blocks = [];
  const used = new Set();
  let i = 0;

  function pushHeading(level, title) {
    const clean = title.trim();
    let id = slug(clean);
    const base = id;
    let n = 2;
    while (used.has(id)) {
      id = base + "-" + n;
      n += 1;
    }
    used.add(id);
    blocks.push({
      type: "heading",
      level: level,
      id: id,
      title: clean,
      plain: clean
    });
  }

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    const image = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/.exec(line);
    if (image) {
      const alt = image[1].trim();
      const src = imageSrc(image[2].trim());
      blocks.push({
        type: "html",
        html:
          '<figure class="manual-photo"><img src="' +
          escapeHtml(src) +
          '" alt="' +
          escapeHtml(alt) +
          '"><figcaption>' +
          inline(alt) +
          "</figcaption></figure>",
        plain: alt
      });
      i += 1;
      continue;
    }

    const heading = /^(#{2,4})\s+(.+)$/.exec(line);
    if (heading) {
      pushHeading(heading[1].length, heading[2]);
      i += 1;
      continue;
    }

    if (line.startsWith(">")) {
      const quote = [];
      while (i < lines.length && lines[i].startsWith(">")) {
        quote.push(lines[i].replace(/^>\s?/, ""));
        i += 1;
      }
      const text = quote.join(" ");
      blocks.push({
        type: "html",
        html: "<blockquote><p>" + inline(text) + "</p></blockquote>",
        plain: text
      });
      continue;
    }

    if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i]);
        i += 1;
      }
      blocks.push(parseTable(rows));
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const match = /^(\s*)[-*]\s+(.*)$/.exec(lines[i]);
        const level = Math.min(2, Math.floor(match[1].replace(/\t/g, "    ").length / 2));
        items.push({ level: level, text: match[2] });
        i += 1;
      }
      blocks.push(renderBulletList(items));
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({
        type: "html",
        html: "<ol>" + items.map((item) => "<li>" + inline(item) + "</li>").join("") + "</ol>",
        plain: items.join("\n")
      });
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      i += 1;
      continue;
    }

    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].startsWith("#") &&
      !lines[i].startsWith(">") &&
      !lines[i].startsWith("|") &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !/^---+$/.test(lines[i].trim())
    ) {
      para.push(lines[i].trim());
      i += 1;
    }
    if (!para.length) {
      i += 1;
      continue;
    }
    const text = para.join(" ");
    blocks.push({
      type: "html",
      html: "<p>" + inline(text) + "</p>",
      plain: text
    });
  }

  return blocks;
}

export function parseManual(markdown) {
  const blocks = parseBlocks(markdown);
  const sections = [];
  let current = null;

  function start(id, title) {
    current = {
      id: id,
      title: title,
      blocks: [],
      plainParts: [title]
    };
    sections.push(current);
  }

  blocks.forEach((block) => {
    if (block.type === "heading" && block.level === 2) {
      start(block.id, block.title);
      current.blocks.push(block);
      return;
    }
    if (!current) start("uvod", "Úvod");
    current.blocks.push(block);
    if (block.plain) current.plainParts.push(block.plain);
    if (block.type === "heading") current.plainParts.push(block.title);
  });

  sections.forEach((section) => {
    section.plain = section.plainParts.join("\n");
  });

  const toc = [];
  sections.forEach((section) => {
    section.blocks.forEach((block) => {
      if (block.type === "heading") {
        toc.push({
          id: block.id,
          title: block.title,
          level: block.level,
          sectionId: section.id
        });
      }
    });
  });

  return { sections: sections, toc: toc };
}

export function highlightHtml(html, query) {
  const tokens = normalizeText(query)
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) return html;
  return String(html)
    .split(/(<[^>]+>)/g)
    .map((part) => (part.startsWith("<") ? part : highlightPlain(part, tokens)))
    .join("");
}

function highlightPlain(text, tokens) {
  const chars = Array.from(text);
  const mark = new Array(chars.length).fill(false);
  const normChars = chars.map((ch) => normalizeText(ch));
  let normStr = "";
  const map = [];
  normChars.forEach((part, index) => {
    for (let k = 0; k < part.length; k += 1) {
      map.push(index);
      normStr += part[k];
    }
  });
  tokens.forEach((token) => {
    let from = 0;
    while (from <= normStr.length - token.length) {
      const at = normStr.indexOf(token, from);
      if (at === -1) break;
      const start = map[at];
      const end = map[at + token.length - 1];
      for (let i = start; i <= end; i += 1) mark[i] = true;
      from = at + token.length;
    }
  });
  let html = "";
  let open = false;
  chars.forEach((ch, index) => {
    if (mark[index] && !open) {
      html += "<mark>";
      open = true;
    }
    if (!mark[index] && open) {
      html += "</mark>";
      open = false;
    }
    html += ch;
  });
  if (open) html += "</mark>";
  return html;
}
